import type {
  ProvinceDTO,
  DistrictDTO,
  ZoneDTO,
  ProvinceCreateInput,
  ProvinceUpdateInput,
  DistrictCreateInput,
  DistrictUpdateInput,
} from "@/modules/geo/domain/contracts";
import type { OffsetPageDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";

export type { ProvinceDTO, DistrictDTO, ZoneDTO };

export const geoApi = {
  zones: {
    async list(): Promise<ZoneDTO[]> {
      return fetcher.get<ZoneDTO[]>(`${config.apiUrl}/geo/zones`);
    },
  },
  provinces: {
    async list(query: OffsetPageQueryDTO): Promise<OffsetPageDTO<ProvinceDTO>> {
      const url = new URL(`${config.apiUrl}/geo/provinces`);
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
      if (query.filters)
        url.searchParams.set("filters", JSON.stringify(query.filters));
      return fetcher.get<OffsetPageDTO<ProvinceDTO>>(url.toString());
    },
    async get(id: string): Promise<ProvinceDTO> {
      return fetcher.get<ProvinceDTO>(`${config.apiUrl}/geo/provinces/${id}`);
    },
    async create(input: ProvinceCreateInput): Promise<ProvinceDTO> {
      return fetcher.post<ProvinceDTO>(
        `${config.apiUrl}/geo/provinces`,
        input,
      );
    },
    async update(id: string, input: ProvinceUpdateInput): Promise<ProvinceDTO> {
      return fetcher.put<ProvinceDTO>(
        `${config.apiUrl}/geo/provinces/${id}`,
        input,
      );
    },
    async remove(id: string): Promise<{ ok: boolean }> {
      return fetcher.delete<{ ok: boolean }>(
        `${config.apiUrl}/geo/provinces/${id}`,
      );
    },
    async toggleEnabled(id: string): Promise<ProvinceDTO> {
      return fetcher.post<ProvinceDTO>(
        `${config.apiUrl}/geo/provinces/${id}/toggle-enabled`,
      );
    },
  },
  districts: {
    async list(query: OffsetPageQueryDTO): Promise<OffsetPageDTO<DistrictDTO>> {
      const url = new URL(`${config.apiUrl}/geo/districts`);
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
      if (query.filters)
        url.searchParams.set("filters", JSON.stringify(query.filters));
      return fetcher.get<OffsetPageDTO<DistrictDTO>>(url.toString());
    },
    async get(id: string): Promise<DistrictDTO> {
      return fetcher.get<DistrictDTO>(`${config.apiUrl}/geo/districts/${id}`);
    },
    async create(input: DistrictCreateInput): Promise<DistrictDTO> {
      return fetcher.post<DistrictDTO>(
        `${config.apiUrl}/geo/districts`,
        input,
      );
    },
    async update(id: string, input: DistrictUpdateInput): Promise<DistrictDTO> {
      return fetcher.put<DistrictDTO>(
        `${config.apiUrl}/geo/districts/${id}`,
        input,
      );
    },
    async remove(id: string): Promise<{ ok: boolean }> {
      return fetcher.delete<{ ok: boolean }>(
        `${config.apiUrl}/geo/districts/${id}`,
      );
    },
    async toggleEnabled(id: string): Promise<DistrictDTO> {
      return fetcher.post<DistrictDTO>(
        `${config.apiUrl}/geo/districts/${id}/toggle-enabled`,
      );
    },
  },
};
